import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getRedemptionById, updateRedemptionRefIdById } from '@services/redemption.service';
import { IRedemption } from '@interfaces/redemption';
import { API_STATUS } from '@constants/api.constants';

export const fetchRedemption = createAsyncThunk('redemption/fetchRedemption', async ({ id, redemptionType }: { id: string; redemptionType: string }) => {
	return await getRedemptionById({ id, redemptionType });
});

export const updateRedemptionMerchantRefIdById = createAsyncThunk(
	'transaction/updateTransactionMerchantRefIdById',
	async ({ id, refId, redemptionType }: { id: string; refId: string; redemptionType: string }) => {
		try {
			const response = await updateRedemptionRefIdById({ id, refId, redemptionType });
			if (response?.status === API_STATUS.ERROR) {
				throw new Error(response?.code);
			}
			return response;
		} catch (error: any) {
			throw new Error(error);
		}
	},
);

export interface RedemptionState {
	redemption: Partial<IRedemption>;
	isLoading: boolean;
	isRefreshing: boolean;
	error: string | null;
}

const initialState = {
	redemption: {},
	isLoading: true,
	isRefreshing: false,
	error: null,
} as RedemptionState;

const redemptionSlice = createSlice({
	name: 'redemption',
	initialState: initialState,
	reducers: {
		fetchRedemption: (state, action: PayloadAction<RedemptionState>) => {
			return {
				...state.redemption,
				...action.payload,
			};
		},
		setRefreshing: (state, action: PayloadAction<boolean>) => {
			state.isRefreshing = action.payload;
		},
	},
	extraReducers(builder) {
		builder.addCase(fetchRedemption.pending, (state: RedemptionState) => {
			state.isLoading = true;
		});
		builder.addCase(fetchRedemption.fulfilled, (state: RedemptionState, action) => {
			state.redemption = action.payload as Partial<IRedemption>;
			state.isRefreshing = false;
			state.isLoading = false;
		});
		builder.addCase(fetchRedemption.rejected, (state: RedemptionState) => {
			state.isLoading = false;
			state.isRefreshing = false;
		});
		builder.addCase(updateRedemptionMerchantRefIdById.pending, (state: RedemptionState) => {
			state.isLoading = true;
			state.error = null;
		});
		builder.addCase(updateRedemptionMerchantRefIdById.fulfilled, (state: RedemptionState, action) => {
			state.redemption.refId = action.meta.arg.refId;
			state.isLoading = false;
		});
		builder.addCase(updateRedemptionMerchantRefIdById.rejected, (state: RedemptionState, action) => {
			state.isLoading = false;
			state.error = action?.error?.message || '';
		});
	},
});

export const redemptionDetailReducer = redemptionSlice.reducer;
export const { setRefreshing } = redemptionSlice.actions;
export default redemptionSlice;
